import React, { useContext, useState } from 'react';
import { Box } from '@material-ui/core';
import { VariantType } from 'notistack';
import { ApplicationContext } from '../../context/Contexts';
import { useStyles } from '../../custom-forms';
import { CustomButton } from '../../custom-forms';
import { buildAltCalcGridColumns } from '../alternative-calculation/config/altCalcGridColumns';
import { buildGridTemplate } from '../alternative-calculation/config/columns';
import AlternativeCalculationChips from '../alternative-calculation/components/AlternativeCalculationChips';
import AlternativeCalculationPiecesHeader from '../alternative-calculation/components/AlternativeCalculationPiecesHeader';
import AlternativeCalculationPart from '../alternative-calculation/components/AlternativeCalculationPart';
import { IReferential } from '../../interfaces/IReferential';
import { Organization } from '../../interfaces/Organization';
import { CaseCalculationPart } from '../../interfaces/Case';
import { WorkflowCalculation } from '../../interfaces/Workflow';
import {
  CalculationActions,
  ColumnFilter,
  ColumnKey,
  FiltersState,
  SortState
} from '../alternative-calculation/types/alternative-calculation.types';
import {
  filterOptionLabel,
  filterOptionsFor
} from '../alternative-calculation/utils/calculation.utils';

export interface UpdateOrderPartContentProps {
  part: CaseCalculationPart;
  qualities: IReferential[];
  suppliers: Organization[];
  workflowCalculation: WorkflowCalculation | null;
  actions: CalculationActions;
  onNotify: (message: string, variant: VariantType) => void;
  variantsEnabled: boolean;
  isOffline: boolean;
  country: IReferential | null;
  orderedInfo: React.ReactNode;
  onSave: () => void;
  executing: boolean;
}

const noLoop = () => undefined;

const UpdateOrderPartContent: React.FC<UpdateOrderPartContentProps> = ({
  part,
  qualities,
  suppliers,
  workflowCalculation,
  actions,
  onNotify,
  variantsEnabled,
  isOffline,
  country,
  orderedInfo,
  onSave,
  executing
}) => {
  const context = useContext(ApplicationContext);
  const translatorService = context.translatorService;
  const c = useStyles();

  const [sort, setSort] = useState<SortState>({ column: null, dir: 1 });
  const [filters, setFilters] = useState<FiltersState>({});

  const showNetPrice = workflowCalculation?.alternativeNetPrice ?? false;
  const gridStyle = {
    ['--alt-grid-cols']: buildGridTemplate(showNetPrice, variantsEnabled)
  } as React.CSSProperties;

  const onSort = (key: ColumnKey) =>
    setSort((prev) =>
      prev.column !== key
        ? { column: key, dir: 1 }
        : prev.dir === 1
        ? { column: key, dir: -1 }
        : { column: null, dir: 1 }
    );

  const onApplyFilter = (key: ColumnKey, value: ColumnFilter | null) =>
    setFilters((prev) => {
      const next = { ...prev };
      if (value == null) delete next[key];
      else next[key] = value;
      return next;
    });

  const onRemoveFilter = (key: ColumnKey) =>
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const onClearFilters = () => setFilters({});

  const onUnselectAll = () => {
    actions.unselectAll();
    onNotify(
      translatorService.Tranlate(
        'ALT_CALC_UNSELECTED_ALL',
        'Selecția a fost anulată pentru toate piesele'
      ),
      'info'
    );
  };

  // Built once — stable identity keeps the memoized part headers from reconciling.
  const [altColumns] = useState(() =>
    buildAltCalcGridColumns({
      workflowCalculation,
      qualities,
      actions,
      onCopyPart: noLoop,
      c,
      t: (key, def) => translatorService.Tranlate(key, def),
      country,
      variantsEnabled,
      showNetPrice,
      caseIsOffline: isOffline
    })
  );

  return (
    <Box p={2}>
      <AlternativeCalculationChips
        filters={filters}
        onRemove={onRemoveFilter}
        onClearAll={onClearFilters}
      />
      <Box style={{ overflowX: 'auto' }}>
        <Box style={{ minWidth: 'min-content', ...gridStyle }}>
          <AlternativeCalculationPiecesHeader
            sort={sort}
            filters={filters}
            onSort={onSort}
            onApplyFilter={onApplyFilter}
            filterOptionsFor={(key) => filterOptionsFor(part ? [part] : [], key, filters)}
            filterOptionLabel={filterOptionLabel}
            onUnselectAll={onUnselectAll}
            hasUnselectAllColumn={true}
            hasRecoColumn={true}
            columns={altColumns}
            flush={false}
          />
          <AlternativeCalculationPart
            part={part}
            sort={sort}
            filters={filters}
            suppliers={suppliers}
            qualities={qualities}
            workflowCalculation={workflowCalculation}
            actions={actions}
            onNotify={onNotify}
            onCopyPart={noLoop}
            enableLineFilterReset={false}
            alternativePartsHeight={800}
            infoTooltip={orderedInfo}
            columns={altColumns}
            canExpand={true}
            flush={false}
            variantsHeader={null}
          />
        </Box>
      </Box>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <CustomButton onClick={onSave} disabled={executing} isLoading={executing}>
          {translatorService.Tranlate('SAVE', 'Salveaza')}
        </CustomButton>
      </Box>
    </Box>
  );
};

export default UpdateOrderPartContent;
